import { type FormEvent, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { isEmailValid, isPasswordValid } from 'dash-common';
import {
  Button,
  Form,
  FormErrorTypes,
  Input,
  InputPassword,
  useFormState,
  useFormValidation,
  ViewerContext,
  localStorage,
  useBackendErrorMessage,
} from 'dash-ui';

import { RoutesPath } from 'flow-components/routes/routes';
import {
  type Account,
  useRegisterDeveloperUserMutation,
} from 'gql/generated.graphql';

import { FormFields, FormFieldsList } from './constants';

const SignUpForm = ({
  account,
  code,
}: {
  code: string;
  account?: Partial<Account>;
}) => {
  const { t } = useTranslation('forms');
  const translate = useBackendErrorMessage();
  const [formErrors, , [setEmailError, setPasswordError]] =
    useFormValidation(FormFieldsList);
  const navigate = useNavigate();

  const [
    formState,
    [setEmail, setPassword],
    [formOnSubmit, formOnError, formOnSuccess],
  ] = useFormState(FormFieldsList, { email: account?.email });

  const validEmail = useCallback(
    (value: string) => {
      // Handle no value
      if (!value) {
        setEmailError({
          type: FormErrorTypes.Info,
          message: t`messages.emailRequired`,
        });
        return false;
      }

      // Handle invalid value
      if (!isEmailValid(value)) {
        setEmailError({
          type: FormErrorTypes.Invalid,
          message: t`messages.emailInvalid`,
        });
        return false;
      }

      setEmailError(null);

      return true;
    },
    [setEmailError, t]
  );

  const validPassword = useCallback(
    (value: string) => {
      // Handle no value
      if (!value) {
        setPasswordError({
          type: FormErrorTypes.Info,
          message: t`messages.passwordRequired`,
        });
        return false;
      }

      // Handle invalid value
      if (!isPasswordValid(value)) {
        setPasswordError({
          type: FormErrorTypes.Invalid,
          message: t`messages.passwordInvalid`,
        });
        return false;
      }

      setPasswordError(null);
      return true;
    },
    [setPasswordError, t]
  );

  const { setAccount } = useContext(ViewerContext);

  const [registerOrganizationUserMutation, { loading }] =
    useRegisterDeveloperUserMutation({
      onError(error) {
        formOnError(translate(error));
      },
      onCompleted: (data) => {
        const { registerDeveloperUser } = data;
        const { accessToken, refreshToken, refreshTokenExpiry } =
          registerDeveloperUser;
        localStorage.setAccessToken(accessToken);
        localStorage.setRefreshToken(refreshToken);
        localStorage.setRefreshTokenExpiry(refreshTokenExpiry || '');
        window.dispatchEvent(new Event('storage'));

        formOnSuccess();

        if (account && setAccount) {
          setAccount(account);
          navigate(RoutesPath.Home);
        }
      },
    });

  const onChangeEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      validEmail(e.target.value);
    },
    [setEmail, validEmail]
  );

  const onChangePassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
      validPassword(e.target.value);
    },
    [setPassword, validPassword]
  );

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      formOnSubmit();
      const { email, password } = formState;

      if (!validEmail(email) || !validPassword(password)) {
        return null;
      }

      registerOrganizationUserMutation({
        variables: { input: { email, password }, signUpVerificationCode: code },
      });

      return null;
    },
    [
      code,
      formOnSubmit,
      formState,
      registerOrganizationUserMutation,
      validEmail,
      validPassword,
    ]
  );

  return (
    <Form onSubmit={onSubmit} w={334}>
      <Form.Group mt={4}>
        <Form.Label>{t`labels.email`}</Form.Label>
        <Input
          w="100%"
          type="email"
          placeholder={t`placeholders.email` || ''}
          onChange={onChangeEmail}
          $errorType={formErrors[FormFields.Email]?.type}
          defaultValue={account?.email}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Email]?.type} mt={1}>
          {formErrors[FormFields.Email]?.message}
        </Form.Feedback>
      </Form.Group>

      <Form.Group mt={4}>
        <Form.Label>{t`labels.password`}</Form.Label>
        <InputPassword
          w="100%"
          placeholder={t`placeholders.password` || ''}
          onChange={onChangePassword}
          $errorType={formErrors[FormFields.Password]?.type}
        />
        <Form.Feedback
          $errorType={formErrors[FormFields.Password]?.type}
          mt={1}
        >
          {formErrors[FormFields.Password]?.message}
        </Form.Feedback>
      </Form.Group>

      <Form.Group mt={4}>
        <Button w="100%" type="submit" disabled={loading} isLoading={loading}>
          {t('pages:organizationSignUpPage:submitButtonLabel')}
        </Button>
      </Form.Group>
    </Form>
  );
};

export default SignUpForm;
